export const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export type QueryValue = string | number | boolean | null | undefined | readonly string[];

/**
 * Build query params from a plain object, applying the conventions every feature
 * used to hand-roll: `undefined`/`null`/`""` and `false` are dropped, `true`
 * emits `"true"`, arrays repeat the key, everything else is stringified (so `0`
 * is kept). Returns a `URLSearchParams` — it stringifies inside template literals
 * and exposes `.get`/`.has`/`.getAll` for tests. Key order follows the object.
 */
export function toQuery(params: Record<string, QueryValue>): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "" || value === false) continue;
    if (value === true) {
      sp.set(key, "true");
    } else if (Array.isArray(value)) {
      for (const v of value) sp.append(key, v);
    } else {
      sp.set(key, String(value));
    }
  }
  return sp;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`API ${status}`);
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init.headers },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
